/**
 * Upsell de planes al final del alta — ADR-2026-07-29-upsell-planes-salir-alta-condicionado / ADR-2026-07-29-presentacion-bottom-sheet-mobile-tocar.
 *
 * Se muestra en `/configuracion-cuenta/resultados` cuando el usuario **descargó**
 * alguna de las imágenes generadas y toca "Seguir explorando Atlas": antes de
 * seguir al alta de la empresa ve el catálogo. Si no descargó nada, no aparece.
 *
 * Monta el **mismo** `PricingCatalog` que `/subscription` (no una copia), en
 * `variant="sheet"`: bottom sheet en mobile, overlay de siempre en desktop.
 *
 * **A quien YA TIENE PLAN PAGO no se le muestra.** Lo decide `abreElCatalogoDePlanes`
 * desde la pantalla de resultados: con el alta por link invertida, el que llega ahí puede
 * haber pagado hace tres minutos, y este catálogo le ofrecía el plan que acababa de
 * comprar (ADR-2026-09-22-el-cuestionario-no-le-vende-a-quien-ya-pago). Por eso el
 * `currentPlan: 'gratis'` de más abajo sigue siendo cierto para todo el que llega.
 *
 * **Es informativo:** acá no se cobra. **Cualquier** botón —suscribirse, cerrar u
 * omitir— continúa el flujo normal, y el pago de verdad se hace después desde la app,
 * donde hay país declarado y empresa a la que colgarle la suscripción. (Este bloque decía
 * que el usuario "todavía no tiene `company_profiles`"; eso dejó de ser cierto cuando la
 * empresa pasó a crearse al terminar el paso 3 —
 * ADR-2026-07-31-onboarding-nuevo-es-el-flujo-real — y con el alta por link ni siquiera
 * eso: nace al registrarse.)
 *
 * Los precios se muestran con el país **inferido del browser**
 * (`lib/paisDelBrowser`), que es una heurística de display: sin ella un usuario
 * argentino —el grueso— vería USD.
 */
import { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { Icon } from '../../design-system/icons';
import PricingCatalog from './PricingCatalog';
import { buildPlanViews } from './buildPlanViews';
import { PLAN_ORDER, ULTRA_TIERS } from './pricingPlans';
import { RECOMMENDED_PLAN, type PricingPlanKey } from './pricingContent';
import { PRICING_COPY_ONBOARDING } from './pricingContexts';
import {
  getPlanById,
  getPlanPriceForPais,
  formatPriceForPais,
} from '../../lib/checkoutPlans';
import { paisDelBrowser } from '../../lib/paisDelBrowser';
import { fetchHotSaleCoupon, type AppliedCoupon } from '../../lib/hotSaleCoupon';

/** WhatsApp de soporte, el mismo que usa `/subscription`. */
const WHATSAPP_URL = `https://wa.me/5491122635392?text=${encodeURIComponent(
  'Buenas, tengo una duda sobre los planes de Atlas.',
)}`;

export interface SetupPricingSheetProps {
  /**
   * Se llama cuando el usuario sale del sheet por cualquiera de sus salidas
   * (cerrar, arrastrar hacia abajo, "Omitir por ahora" o el CTA de suscripción).
   * En mobile llega **después** de la animación de salida.
   */
  onDismiss: () => void;
}

export function SetupPricingSheet({ onDismiss }: SetupPricingSheetProps) {
  // Se resuelve una sola vez: el timezone no cambia mientras el sheet está
  // abierto y no queremos que un re-render mueva los precios.
  const pais = useMemo(() => paisDelBrowser() ?? '', []);
  const [selected, setSelected] = useState<PricingPlanKey>(RECOMMENDED_PLAN);
  const [tierIndex, setTierIndex] = useState(0);

  // El MISMO cupón invisible que `/subscription` y el sheet de "sin créditos"
  // (`lib/hotSaleCoupon`). Antes acá iba `coupon: null` y este catálogo —el PRIMERO
  // que ve el usuario, justo en el momento de más intención— mostraba Pro y Ultra a
  // precio de lista, mientras que los otros dos puntos de venta mostraban el descuento
  // vigente. El descuento es real y le va a aplicar cuando pague: mostrarlo acá no
  // promete nada que el checkout no cobre.
  // Ver ADR-2026-08-03-upsell-del-alta-muestra-el-mismo-descuento.
  const [coupon, setCoupon] = useState<AppliedCoupon | null>(null);
  useEffect(() => {
    let cancelado = false;
    fetchHotSaleCoupon().then((c) => {
      if (!cancelado) setCoupon(c);
    });
    return () => {
      cancelado = true;
    };
  }, []);

  const plans = buildPlanViews({
    // Sin empresa no hay plan contratado: todas las cards son un alta.
    currentPlan: 'gratis',
    // Este sheet es parte del onboarding: la cuenta se acaba de crear, así que no
    // existe el caso "ex cliente que volvió a gratis" que
    // ADR-2026-08-18-descuento-no-revive-al-volver-a-gratis viene a cerrar. Se
    // pasa fijo en vez de consultar la empresa —que ni siquiera existe todavía—
    // y el checkout, que sí COBRA, lo verifica por su cuenta contra la base.
    yaFueClientePago: false,
    ultraTiers: ULTRA_TIERS,
    ultraTierIndex: tierIndex,
    coupon,
    arsBaseFor: (key) => getPlanById(key)?.price ?? 0,
    getPlanPrice: (planId, ars) => getPlanPriceForPais(planId, ars, pais),
    formatPrice: (amount) => formatPriceForPais(amount, pais),
    planOrder: PLAN_ORDER,
    processing: false,
    // Sin plan vigente no hay nada que cancelar: `buildPlanViews` no llega a
    // usarlo (`canCancel` es false para 'gratis').
    onCancel: () => {},
  });

  return createPortal(
    <>
      <PricingCatalog
      variant="sheet"
      contextCopy={PRICING_COPY_ONBOARDING}
      plans={plans}
      selected={selected}
      onSelect={setSelected}
      onSubscribe={onDismiss}
      processing={false}
      onClose={onDismiss}
      skipLabel="Omitir por ahora"
      onSkip={onDismiss}
      ultraTier={{
        index: tierIndex,
        count: ULTRA_TIERS.length,
        onChange: setTierIndex,
      }}
      // El switch Mensual/Anual es sólo para cuentas `dev_tester` (ADR-2026-07-28-switch-mensual-anual-visual-solo) y
      // acá todavía no hay perfil de empresa que consultar.
      showBillingToggle={false}
      annual={false}
      onAnnualChange={() => {}}
      onHelp={() => window.open(WHATSAPP_URL, '_blank', 'noopener,noreferrer')}
      note={
        <div className="flex flex-col items-center gap-4">
          <p className="max-w-xl">
            Podés empezar gratis y suscribirte cuando quieras desde tu cuenta.
          </p>
          <button
            type="button"
            onClick={() => window.open(WHATSAPP_URL, '_blank', 'noopener,noreferrer')}
            className="inline-flex items-center gap-2 rounded-full border border-white/30 px-4 py-2 text-white/90"
          >
            <Icon name="outline/whatsapp" size={16} className="shrink-0" />
            ¿Tenés alguna duda? Escribinos
          </button>
        </div>
      }
      />
    </>,
    // Portal a `body`: el shell de la app (`DashboardLayout`, que envuelve
    // resultados en desktop) es `fixed` + `transform-gpu`, y un `fixed` adentro
    // de un ancestro con transform se posiciona respecto de ÉL, no del viewport
    // (mismo motivo que en `Paywall`, ADR-2026-07-28-chrome-paywall-va-anclado-overlay y ADR-2026-07-29-catalogo-paywall-modales-comparten-z).
    document.body,
  );
}

export default SetupPricingSheet;
